// ============================================================================
// ExercisesPage — route /app/exercises  (namespace: "exercises")
// ----------------------------------------------------------------------------
// The exercise catalog: instant search-as-you-type + faceted filter chips
// (muscle · movement pattern · equipment · modality · level), favourites
// pinned to the top, an exercise detail sheet (instructions, muscles, demo
// placeholder), and add-custom-exercise. The whole catalog is fetched ONCE and
// searched/faceted client-side so every keystroke is instant; favourites flow
// through useFavorites + useToggleFavorite (optimistic). The AppShell live-sync
// socket invalidates qk.exercises.* / qk.favorites.* so the catalog and pins
// stay fresh across devices with no reload. Mobile-first (390 first), rich +
// reduced-motion-aware motion throughout.
//
// Routing + i18n are pre-wired; this file stays a DEFAULT export named
// ExercisesPage and only fills its body + the "exercises" i18n namespace.
// ============================================================================
import { useDeferredValue, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, EmptyState } from "../../components";
import { IconPlus, IconStar } from "../../components/icons";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import {
  useExercises,
  useFavorites,
  useToggleFavorite,
} from "../../hooks/exercises";
import type { Exercise } from "../../api";
import type { Locale } from "../../i18n/config";
import { ExerciseCard } from "./ExerciseCard";
import { ExerciseDetail } from "./ExerciseDetail";
import { CustomExerciseForm } from "./CustomExerciseForm";
import { FilterChips } from "./FilterChips";
import type { ExLocale, FacetKey, FilterState } from "./lib";
import {
  activeFilterCount,
  deriveFacets,
  emptyFilters,
  filterExercises,
} from "./lib";

export default function ExercisesPage() {
  const { t, i18n } = useTranslation("exercises");
  const reduce = useReducedMotion();
  const locale: ExLocale = (i18n.language as Locale) === "zh" ? "zh" : "en";

  const catalogQuery = useExercises();
  const favoritesQuery = useFavorites();
  const toggleFav = useToggleFavorite();

  const [query, setQuery] = useState("");
  // Defer the heavy filter pass so typing never stutters on a big catalog.
  const deferredQuery = useDeferredValue(query);
  const [filters, setFilters] = useState<FilterState>(emptyFilters());
  const [showFilters, setShowFilters] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const [detailExercise, setDetailExercise] = useState<Exercise | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const favorites = useMemo(
    () => favoritesQuery.data ?? [],
    [favoritesQuery.data],
  );
  const favoriteIds = useMemo(
    () => new Set(favorites.map((e) => e.id)),
    [favorites],
  );

  const facets = useMemo(() => deriveFacets(catalog), [catalog]);

  // The base set to search: either the whole catalog or just favourites.
  const baseSet = favoritesOnly ? favorites : catalog;

  const results = useMemo(
    () => filterExercises(baseSet, filters, deferredQuery, locale),
    [baseSet, filters, deferredQuery, locale],
  );

  // Favourites pinned to the top when browsing the full catalog with no
  // explicit search/filter narrowing them out.
  const pinned = useMemo(
    () => results.filter((e) => favoriteIds.has(e.id)),
    [results, favoriteIds],
  );
  const rest = useMemo(
    () => results.filter((e) => !favoriteIds.has(e.id)),
    [results, favoriteIds],
  );

  const activeFilters = activeFilterCount(filters);
  const isSearching = deferredQuery.trim().length > 0;
  const showPinnedSection = !favoritesOnly && pinned.length > 0;

  const onToggleFacet = (key: FacetKey, value: string) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));

  const clearAll = () => {
    setFilters(emptyFilters());
    setQuery("");
    setFavoritesOnly(false);
  };

  const openDetail = (ex: Exercise) => {
    setDetailExercise(ex);
    setDetailOpen(true);
  };

  const toggleFavorite = (ex: Exercise) =>
    toggleFav.mutate({ id: ex.id, on: !favoriteIds.has(ex.id) });

  if (catalogQuery.isLoading) {
    return <LoadingBlock label={t("loading")} />;
  }
  if (catalogQuery.isError) {
    return <ErrorBlock onRetry={() => void catalogQuery.refetch()} />;
  }

  const totalCount = catalog.length;
  const resultCount = results.length;

  return (
    <div className="space-y-6">
      {/* --- Header --- */}
      <motion.header
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <p className="font-display text-xs font-bold uppercase tracking-kicker text-body-faint">
            {t("kicker")}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
            {t("title")}
          </h1>
          <p className="mt-1 text-sm text-body-soft">
            {t("subtitle", { count: totalCount })}
          </p>
        </div>
        <Button
          onClick={() => setCustomOpen(true)}
          iconLeft={<IconPlus width={18} height={18} />}
          className="self-start sm:self-auto"
        >
          {t("addCustom")}
        </Button>
      </motion.header>

      {/* --- Search + controls --- */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: reduce ? 0 : 0.05, ease: [0.22, 0.61, 0.36, 1] }}
        className="space-y-3"
      >
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder={t("searchPlaceholder")}
          clearLabel={t("clearSearch")}
        />

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            className="inline-flex items-center gap-2 rounded-pill bg-surface px-4 py-2 font-display text-sm font-bold text-body-muted ring-1 ring-inset ring-line-strong shadow-xs transition-colors hover:bg-surface-sunken hover:text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
          >
            <IconSliders />
            {t("filters")}
            {activeFilters > 0 && (
              <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-pill bg-grad-coral px-1.5 text-[0.68rem] font-extrabold tabular-nums text-white">
                {activeFilters}
              </span>
            )}
          </button>

          <FavoritesToggle
            active={favoritesOnly}
            count={favorites.length}
            label={t("favoritesOnly")}
            onClick={() => setFavoritesOnly((v) => !v)}
          />

          {(activeFilters > 0 || isSearching || favoritesOnly) && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1.5 rounded-pill px-3 py-2 font-display text-sm font-bold text-body-soft transition-colors hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
            >
              {t("clearAll")}
            </button>
          )}

          <span className="ml-auto text-sm font-bold tabular-nums text-body-soft">
            {t("resultCount", { count: resultCount })}
          </span>
        </div>

        <AnimatePresence initial={false}>
          {showFilters && (
            <motion.div
              key="facets"
              initial={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, height: "auto" }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="rounded-lg bg-surface p-5 ring-1 ring-line shadow-sm">
                <FilterChips
                  facets={facets}
                  filters={filters}
                  onToggle={onToggleFacet}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* --- Results --- */}
      {resultCount === 0 ? (
        <EmptyState
          title={
            favoritesOnly && favorites.length === 0
              ? t("empty.noFavoritesTitle")
              : t("empty.title")
          }
          description={
            favoritesOnly && favorites.length === 0
              ? t("empty.noFavoritesBlurb")
              : t("empty.blurb")
          }
          action={
            favoritesOnly && favorites.length === 0 ? (
              <Button variant="ghost" onClick={() => setFavoritesOnly(false)}>
                {t("empty.browseAll")}
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={clearAll}>
                  {t("empty.clear")}
                </Button>
                <Button
                  onClick={() => setCustomOpen(true)}
                  iconLeft={<IconPlus width={18} height={18} />}
                >
                  {t("addCustom")}
                </Button>
              </>
            )
          }
        />
      ) : (
        <div className="space-y-8">
          {showPinnedSection && (
            <Section
              title={t("favorites")}
              accent={<IconStar filled width={18} height={18} />}
              count={pinned.length}
            >
              <Grid
                exercises={pinned}
                locale={locale}
                favoriteIds={favoriteIds}
                favPending={toggleFav.isPending}
                onOpen={openDetail}
                onToggleFavorite={toggleFavorite}
              />
            </Section>
          )}

          <Section
            title={
              favoritesOnly
                ? t("favorites")
                : showPinnedSection
                  ? t("allExercises")
                  : t("catalog")
            }
            count={favoritesOnly ? resultCount : rest.length}
          >
            <Grid
              exercises={favoritesOnly ? results : rest}
              locale={locale}
              favoriteIds={favoriteIds}
              favPending={toggleFav.isPending}
              onOpen={openDetail}
              onToggleFavorite={toggleFavorite}
            />
          </Section>
        </div>
      )}

      {/* --- Sheets --- */}
      <ExerciseDetail
        exercise={detailExercise}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        locale={locale}
        favorite={detailExercise ? favoriteIds.has(detailExercise.id) : false}
        favPending={toggleFav.isPending}
        onToggleFavorite={() =>
          detailExercise && toggleFavorite(detailExercise)
        }
      />
      <CustomExerciseForm
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        catalog={catalog}
        onCreated={(created) => openDetail(created)}
      />
    </div>
  );
}

// --- search bar -------------------------------------------------------------

function SearchBar({
  value,
  onChange,
  placeholder,
  clearLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  clearLabel: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-body-faint">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="m20 20-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
      <input
        type="search"
        inputMode="search"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full rounded-pill bg-surface pl-12 pr-12 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-sm transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
        style={{ height: "3.25rem" }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={clearLabel}
          className="absolute inset-y-0 right-3 my-auto grid h-9 w-9 place-items-center rounded-pill text-body-muted transition-colors hover:bg-surface-sunken hover:text-body"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function FavoritesToggle({
  active,
  count,
  label,
  onClick,
}: {
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "inline-flex items-center gap-2 rounded-pill px-4 py-2 font-display text-sm font-bold transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt " +
        (active
          ? "bg-grad-coral text-white shadow-volt"
          : "bg-surface text-body-muted ring-1 ring-inset ring-line-strong shadow-xs hover:bg-surface-sunken hover:text-body")
      }
    >
      <IconStar filled={active} width={16} height={16} />
      {label}
      <span
        className={
          "tabular-nums text-xs font-extrabold " +
          (active ? "text-white/80" : "text-body-faint")
        }
      >
        {count}
      </span>
    </button>
  );
}

// --- section + grid ---------------------------------------------------------

function Section({
  title,
  count,
  accent,
  children,
}: {
  title: string;
  count: number;
  accent?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        {accent && <span className="text-coral">{accent}</span>}
        <h2 className="font-display text-lg font-bold tracking-tight text-body">
          {title}
        </h2>
        <Badge tone="neutral">{count}</Badge>
      </div>
      {children}
    </section>
  );
}

function Grid({
  exercises,
  locale,
  favoriteIds,
  favPending,
  onOpen,
  onToggleFavorite,
}: {
  exercises: Exercise[];
  locale: ExLocale;
  favoriteIds: Set<number>;
  favPending: boolean;
  onOpen: (ex: Exercise) => void;
  onToggleFavorite: (ex: Exercise) => void;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      layout={!reduce}
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {exercises.map((ex, i) => (
          <motion.div
            key={ex.id}
            layout={!reduce}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
            transition={{
              duration: 0.32,
              delay: reduce ? 0 : Math.min(i, 8) * 0.025,
              ease: [0.22, 0.61, 0.36, 1],
            }}
          >
            <ExerciseCard
              exercise={ex}
              locale={locale}
              favorite={favoriteIds.has(ex.id)}
              favPending={favPending}
              onOpen={() => onOpen(ex)}
              onToggleFavorite={() => onToggleFavorite(ex)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

function IconSliders() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h7M15 17h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="16" cy="7" r="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="8" cy="12" r="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="13" cy="17" r="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
